import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Crown, Check, Sparkles, Lock, Zap, Wallet, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster, toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

/**
 * Public Link Pro landing page. Shows the two new tiered plans (Core, Plus) +
 * prepaid credit packs. Heavy emphasis on "Same price every time".
 */
export default function Plans() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/plans`).then((r) => setPlans(r.data || [])).catch(() => toast.error("Could not load plans"));
    axios.get(`${API}/credit-packs`).then((r) => setPacks(r.data || [])).catch(() => {});
  }, []);

  const startSubscribe = async (planId) => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/plans`);
      return;
    }
    setBusy(`sub_${planId}`);
    try {
      const { data } = await axios.post(`${API}/me/subscribe`, {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Subscription failed");
    } finally {
      setBusy(null);
    }
  };

  const startBuyPack = async (packId) => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/plans`);
      return;
    }
    setBusy(`pack_${packId}`);
    try {
      const { data } = await axios.post(`${API}/me/credits/buy`, {
        pack_id: packId,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pack purchase failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster position="top-center" />
      <header className="px-5 pt-6 pb-4 flex items-center justify-between border-b border-white/5">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm"
          data-testid="plans-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-[#FFD369]" />
          <span className="text-xs uppercase tracking-[0.25em] text-white/70 font-bold">Link Pro</span>
        </div>
        <div className="w-10" />
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10 space-y-12">
        {/* Hero — same-price-every-time */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30">
            <Lock className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-bold">
              Built For Shift Workers
            </span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]"
            style={{ fontFamily: "Outfit, system-ui" }}
            data-testid="plans-headline"
          >
            Lock your rate.<br />
            <span className="text-[#4D88FF]">$15 stays $15 — even on Saturday night.</span>
          </h1>
          <p className="text-base text-white/60 max-w-md mx-auto">
            Built for the people who need a ride to work, not a $25 fare because it&apos;s a busy weekend. Lock your price with Link Pro and the fare you see at sign-up is the fare you always pay — every shift, every Friday night, every holiday.
          </p>
        </div>

        {/* Subscription tiers */}
        <section>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">
            Membership · Save on every trip
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {plans.map((p) => {
              const featured = p.id === "plus";
              const Icon = featured ? Zap : Lock;
              return (
                <Card
                  key={p.id}
                  className={`relative overflow-hidden transition-colors ${
                    featured
                      ? "bg-gradient-to-br from-[#0047FF]/25 via-[#0a0a0a] to-[#0a0a0a] border-[#0047FF]/50 shadow-[0_4px_60px_-10px_rgba(0,71,255,0.5)]"
                      : "bg-[#121212] border-white/10"
                  }`}
                  data-testid={`public-plan-${p.id}`}
                >
                  {featured && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-[#FFD369] border-2 border-[#050505] shadow-lg">
                      <span className="text-[9px] uppercase tracking-wider text-black font-black">Most Popular</span>
                    </div>
                  )}
                  <CardContent className="p-6 space-y-5">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className={`w-5 h-5 ${featured ? "text-[#4D88FF]" : "text-emerald-400"}`} />
                        <p className="text-sm uppercase tracking-[0.18em] text-white/70 font-bold">
                          {p.name.replace("Link Pro ", "")}
                        </p>
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-5xl font-black text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                          ${(p.price_cents / 100).toFixed(2)}
                        </span>
                        <span className="text-sm text-white/50">/month</span>
                      </div>
                      <p className="text-xs text-white/55 mt-3 leading-relaxed">{p.description}</p>
                    </div>
                    <div className="space-y-2.5 text-sm text-white/85">
                      {(p.perks || []).map((perk, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${featured ? "text-[#4D88FF]" : "text-emerald-400"}`} />
                          <span>{perk}</span>
                        </div>
                      ))}
                    </div>
                    <Button
                      onClick={() => startSubscribe(p.id)}
                      disabled={busy === `sub_${p.id}`}
                      className={`w-full py-6 font-bold rounded-xl border-0 ${
                        featured
                          ? "bg-[#0047FF] hover:bg-[#003BE0] text-white shadow-[0_4px_30px_-8px_rgba(0,71,255,0.7)]"
                          : "bg-white/10 hover:bg-white/15 text-white"
                      }`}
                      data-testid={`public-subscribe-${p.id}`}
                    >
                      {busy === `sub_${p.id}`
                        ? "Starting checkout…"
                        : isAuthenticated
                          ? `Get ${p.name.replace("Link Pro ", "")}`
                          : "Sign in & subscribe"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Credit packs */}
        {packs.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">
                Prepaid Credit Packs · No subscription
              </p>
              <span className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">
                Bonus credits included
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {packs.map((pack) => {
                const featured = pack.id === "pack_100";
                return (
                  <Card
                    key={pack.id}
                    className={`relative overflow-hidden transition-colors ${
                      featured
                        ? "bg-gradient-to-br from-amber-500/15 via-[#0a0a0a] to-[#0a0a0a] border-amber-500/40 shadow-[0_4px_40px_-10px_rgba(245,158,11,0.4)]"
                        : "bg-[#121212] border-white/10"
                    }`}
                    data-testid={`pack-${pack.id}`}
                  >
                    {featured && (
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-amber-400 border-2 border-[#050505] shadow-lg">
                        <span className="text-[9px] uppercase tracking-wider text-black font-black">Best Deal</span>
                      </div>
                    )}
                    <CardContent className="p-6 space-y-4">
                      <div className="flex items-center gap-2">
                        <Wallet className={`w-5 h-5 ${featured ? "text-amber-400" : "text-white/70"}`} />
                        <span className="text-sm uppercase tracking-[0.18em] text-white/70 font-bold">
                          Pay ${(pack.price_cents / 100).toFixed(0)}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-black text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                            ${(pack.credit_cents / 100).toFixed(0)}
                          </span>
                          <span className="text-sm text-white/50">in ride credits</span>
                        </div>
                        <p className="text-xs text-amber-300 font-bold mt-1">
                          + ${((pack.credit_cents - pack.price_cents) / 100).toFixed(0)} bonus credit
                          {" "}(+{pack.bonus_pct}%)
                        </p>
                        <p className="text-[11px] text-white/50 mt-1">
                          Credits apply at checkout
                        </p>
                      </div>
                      <div className="space-y-2 text-xs text-white/65">
                        <div className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-400 flex-shrink-0" />
                          <span>Use on any ride or delivery</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-400 flex-shrink-0" />
                          <span>Never expires</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-400 flex-shrink-0" />
                          <span>One-time payment, no recurring</span>
                        </div>
                      </div>
                      <Button
                        onClick={() => startBuyPack(pack.id)}
                        disabled={busy === `pack_${pack.id}`}
                        className={`w-full py-5 font-bold rounded-xl border-0 ${
                          featured
                            ? "bg-amber-500 hover:bg-amber-600 text-black"
                            : "bg-white/10 hover:bg-white/15 text-white"
                        }`}
                        data-testid={`buy-pack-${pack.id}`}
                      >
                        {busy === `pack_${pack.id}` ? "Starting checkout…" : `Buy now`}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* Loyalty teaser */}
        <section className="rounded-2xl p-6 bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/30">
              <Gift className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                Earn loyalty points on every trip
              </h3>
              <p className="text-sm text-white/60 mt-1.5">
                Get <strong className="text-emerald-300">1 point per $1 spent</strong> · <strong className="text-emerald-300">1.5× as a Link Pro member</strong>. Redeem for ride credits at 100, 250, and 500 points.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/70">100 pts → $5</span>
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/70">250 pts → $15</span>
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 font-bold">500 pts → $35</span>
              </div>
            </div>
          </div>
        </section>

        <div className="text-center text-xs text-white/40 pt-2">
          <p>Secure payments by Stripe · Cancel from your Account page anytime</p>
          <button
            onClick={() => navigate("/")}
            className="mt-4 text-white/60 hover:text-white underline underline-offset-4"
            data-testid="plans-skip-btn"
          >
            Skip · Just book a trip
          </button>
        </div>
      </main>
    </div>
  );
}
